// src/App.tsx

import { Suspense, lazy } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { ThemeProvider } from "@/components/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

import AppLayout from "@/components/AppLayout";
import HealthCheck from "@/components/HealthCheck";
import PortalLayout from "@/components/PortalLayout";
import ResetPassword from "@/pages/ResetPassword";

import Index from "@/pages/Index";
import News from "@/pages/News";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Retailers from "@/pages/Retailers";
import About from "@/pages/About";
import AboutFR from "@/pages/AboutFR";
import AboutES from "@/pages/About.es";

import RetailersFR from "@/pages/RetailersFR";
import RetailersES from "@/pages/RetailersES";
import RetailersDemo from "@/pages/RetailersDemo";
import AppPage from "@/pages/AppPage";
import RetailersPricing from "@/pages/RetailersPricing";
import HowItWorks from "@/pages/HowItWorks";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
const Manufacturers = lazy(() => import("@/pages/Manufacturers"));

const ContactUs = lazy(() => import("@/pages/ContactUs"));
const Portal = lazy(() => import("@/pages/Portal"));
const PortalProducts = lazy(() => import("@/pages/PortalProducts"));
const PortalOnboarding = lazy(() => import("@/pages/PortalOnboarding"));
const PortalLocations = lazy(() => import("@/pages/PortalLocations"));
const PortalAssets = lazy(() => import("@/pages/PortalAssets"));
const PortalMetrics = lazy(() => import("@/pages/PortalMetrics"));
const PortalSettings = lazy(() => import("@/pages/PortalSettings"));
const PortalSupport = lazy(() => import("@/pages/PortalSupport"));
const PortalBilling = lazy(() => import("@/pages/PortalBilling"));
const PortalCatalogue = lazy(() => import("@/pages/PortalCatalogue"));
const PortalQuoteRequests = lazy(() => import("@/pages/PortalQuoteRequests"));
const PortalEmbed    = lazy(() => import("@/pages/PortalEmbed"));
const WhyFyrris      = lazy(() => import("@/pages/WhyFyrris"));
const FAQ            = lazy(() => import("@/pages/FAQ"));
const Compare        = lazy(() => import("@/pages/Compare"));
const Demo           = lazy(() => import("@/pages/Demo"));

const ManufacturerOverview   = lazy(() => import("@/pages/manufacturer/ManufacturerOverview"));
const ManufacturerProducts   = lazy(() => import("@/pages/manufacturer/ManufacturerProducts"));
const ManufacturerVariants   = lazy(() => import("@/pages/manufacturer/ManufacturerVariants"));
const ManufacturerDealers    = lazy(() => import("@/pages/manufacturer/ManufacturerDealers"));
const ManufacturerGeography  = lazy(() => import("@/pages/manufacturer/ManufacturerGeography"));
const ManufacturerReports    = lazy(() => import("@/pages/manufacturer/ManufacturerReports"));
const ManufacturerBenchmarks = lazy(() => import("@/pages/manufacturer/ManufacturerBenchmarks"));

import AdminLayout from "@/components/AdminLayout"
import ManufacturerLayout from "@/components/ManufacturerLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProducts from "@/pages/AdminProducts";
import AdminProductWizard from "@/pages/AdminProductWizard";
import AdminProductEdit from "@/pages/AdminProductEdit";
import AdminManufacturers from "@/pages/AdminManufacturers";
import AdminParametricDrafts from "@/pages/AdminParametricDrafts";
import AdminManufacturerDetail from "@/pages/AdminManufacturerDetail";
import { RETAILER_IMPERSONATE_KEY } from "@/contexts/AppContext";
import AdminRetailers from "@/pages/AdminRetailers";
import AdminPricingCalculator from "@/pages/AdminPricingCalculator";
import AdminPipeline from "@/pages/AdminPipeline";
import AdminSettings from "@/pages/AdminSettings";
import AdminSendInvoice from "@/pages/AdminSendInvoice";
import AdminCatalogueRequests from "@/pages/AdminCatalogueRequests";
import AdminMaterialLibrary from "@/pages/AdminMaterialLibrary";
import AdminQuality from "@/pages/AdminQuality";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminFeatureFlags from "@/pages/AdminFeatureFlags";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import PortalWelcome from "@/pages/PortalWelcome";
import NotFound from "@/pages/NotFound";

import { Analytics } from "@vercel/analytics/react";

import { AppProvider, useAppContext } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { ImpersonationProvider, useImpersonation } from "@/contexts/ImpersonationContext";
import ImpersonationBanner from "@/components/ImpersonationBanner";

function Fallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-400">
      Loading…
    </div>
  );
}

const queryClient = new QueryClient();

/* ---------------------------
   Guards
---------------------------- */

function GuardSpinner() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-gray-400">
      Loading…
    </div>
  );
}

/**
 * Auth guard: requires a Supabase session.
 * Must check AuthContext loading — session is null during init and
 * would cause a false redirect to /signin on every cold page load.
 */
function RequireAuth() {
  const { session, loading } = useAuth();

  if (loading) return <GuardSpinner />;
  if (!session) return <Navigate to="/signin" replace />;
  return <Outlet />;
}

/**
 * Active subscription guard.
 * Bypassed when an admin is impersonating (they see portal regardless of isActive).
 */
function RequireActive() {
  const { session, loading: authLoading } = useAuth();
  const { isActive, isAdmin, loading: appLoading } = useAppContext();
  const { impersonating } = useImpersonation();

  if (authLoading || appLoading) return <GuardSpinner />;
  if (!session) return <Navigate to="/signin" replace />;
  // Impersonating admins (old system or new localStorage system) bypass activation
  if (impersonating) return <Outlet />;
  if (isAdmin && !!localStorage.getItem(RETAILER_IMPERSONATE_KEY)) return <Outlet />;
  if (!isActive) return <Navigate to="/portal/billing" replace />;
  return <Outlet />;
}

/**
 * Admin guard: requires session + admin role.
 */
function RequireAdmin() {
  const { session, loading: authLoading } = useAuth();
  const { isAdmin, loading: appLoading } = useAppContext();

  if (authLoading || appLoading) return <GuardSpinner />;
  if (!session) return <Navigate to="/signin" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <Outlet />;
}

const MFR_IMPERSONATE_KEY = "fyrris_impersonate_manufacturer_id";

/**
 * Manufacturer portal guard.
 * Passes through if: (a) user has a manufacturer_users row, OR
 * (b) user is an admin AND localStorage has an impersonation key set.
 */
function RequireManufacturer() {
  const { isManufacturerUser, isAdmin, loading: appLoading } = useAppContext();
  if (appLoading) return <GuardSpinner />;
  const hasImpersonate = isAdmin && !!localStorage.getItem(MFR_IMPERSONATE_KEY);
  if (!isManufacturerUser && !hasImpersonate) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * Redirects admin users away from /portal to /admin.
 * Skipped when the admin is actively impersonating a retailer.
 */
function AdminRedirect() {
  const { loading: authLoading } = useAuth();
  const { isAdmin, loading: appLoading } = useAppContext();
  const { impersonating } = useImpersonation();

  if (authLoading || appLoading) return <GuardSpinner />;
  // Admin impersonating a retailer — let them through to /portal
  if (isAdmin && impersonating) return <Outlet />;
  if (isAdmin) return <Navigate to="/admin" replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <ThemeProvider defaultTheme="dark">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <HealthCheck />

          <AppProvider>
            <ImpersonationProvider>
            <ImpersonationBanner />
            <Suspense fallback={<Fallback />}>
              <Routes>
                {/* Public site */}
                <Route element={<AppLayout />}>
                  {/* English */}
                  <Route path="/" element={<Index />} />
                  <Route path="/news" element={<News />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  <Route path="/ar-demo" element={<Navigate to="/retailers" replace />} />
                  <Route path="/retailers" element={<Retailers />} />
                  <Route path="/retailers/pricing" element={<RetailersPricing />} />
                  <Route path="/retailers/demo" element={<RetailersDemo />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/why-fyrris" element={<WhyFyrris />} />
                  <Route path="/faq" element={<FAQ />} />
                  {/* /compare/:slug self-noindexes while its entry is a draft. See
                      src/data/comparisons.ts. */}
                  <Route path="/compare/:slug" element={<Compare />} />
                  <Route path="/app" element={<AppPage />} />
                  <Route path="/manufacturers" element={<Manufacturers />} />

                  {/* French */}
                  <Route path="/fr" element={<Index />} />
                  <Route path="/fr/nouvelles" element={<News />} />
                  <Route path="/fr/comment-ca-marche" element={<HowItWorks />} />
                  <Route path="/fr/ar-demo" element={<Navigate to="/fr/detaillants" replace />} />
                  <Route path="/fr/detaillants" element={<RetailersFR />} />
                  <Route path="/fr/detaillants/tarifs" element={<RetailersPricing />} />
                  <Route path="/fr/detaillants/demo" element={<RetailersDemo />} />
                  <Route path="/fr/confidentialite" element={<PrivacyPolicy />} />
                  <Route path="/fr/conditions" element={<Terms />} />
                  <Route path="/fr/contact" element={<Contact />} />
                  <Route path="/fr/a-propos" element={<AboutFR />} />
                  <Route path="/fr/app" element={<AppPage />} />
                  <Route path="/fr/fabricants" element={<Manufacturers />} />
                  <Route path="/fr/pourquoi-fyrris" element={<WhyFyrris />} />
                  <Route path="/fr/faq" element={<FAQ />} />

                  {/* Spanish */}
                  <Route path="/es" element={<Index />} />
                  <Route path="/es/como-funciona" element={<HowItWorks />} />
                  <Route path="/es/contacto" element={<Contact />} />
                  <Route path="/es/terminos" element={<Terms />} />
                  <Route path="/es/sobre-nosotros" element={<AboutES />} />
                  <Route path="/es/minoristas" element={<RetailersES />} />
                  <Route path="/es/minoristas/precios" element={<RetailersPricing />} />
                  <Route path="/es/minoristas/demo" element={<RetailersDemo />} />
                  <Route path="/es/app" element={<AppPage />} />
                  <Route path="/es/fabricantes" element={<Manufacturers />} />

                  {/* Auth */}
                  <Route path="/signin" element={<SignIn />} />
                  <Route path="/signup" element={<SignUp />} />
                  <Route path="/fr/inscription" element={<SignUp />} />
                  <Route path="/es/registro" element={<SignUp />} />
                  <Route path="/reset" element={<ResetPassword />} />
                </Route>

                {/* Portal (auth required, non-admin only) */}
                <Route element={<RequireAuth />}>
                  <Route element={<AdminRedirect />}>
                  <Route path="/portal" element={<PortalLayout />}>
                    {/* Require active subscription for most sections */}
                    <Route element={<RequireActive />}>
                      <Route index element={<Portal />} />
                      <Route path="products" element={<PortalProducts />} />
                      <Route path="locations" element={<PortalLocations />} />
                      <Route path="assets" element={<PortalAssets />} />
                      <Route path="metrics" element={<PortalMetrics />} />
                      <Route path="settings" element={<PortalSettings />} />
                      <Route path="support" element={<PortalSupport />} />
                      <Route path="catalogue" element={<PortalCatalogue />} />
                      <Route path="quote-requests" element={<PortalQuoteRequests />} />
                      <Route path="embed" element={<PortalEmbed />} />
                    </Route>

                    {/* Allowed before activation */}
                    <Route path="onboarding" element={<PortalOnboarding />} />
                    <Route path="billing" element={<PortalBilling />} />
                    <Route path="welcome" element={<PortalWelcome />} />
                  </Route>
                  </Route>
                </Route>

                {/* Admin (auth + admin) */}
                <Route element={<RequireAdmin />}>
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="products/new" element={<AdminProductWizard />} />
                    <Route path="products/:id" element={<AdminProductEdit />} />
                    <Route path="products/:id/photos" element={<AdminProductWizard />} />
                    <Route path="manufacturers" element={<AdminManufacturers />} />
                    <Route path="parametric-drafts" element={<AdminParametricDrafts />} />
                    <Route path="manufacturers/:id" element={<AdminManufacturerDetail />} />
                    <Route path="retailers" element={<AdminRetailers />} />
                    <Route path="pricing-calculator" element={<AdminPricingCalculator />} />
                    <Route path="pipeline" element={<AdminPipeline />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="send-invoice"   element={<AdminSendInvoice />} />
                    <Route path="catalogue-requests" element={<AdminCatalogueRequests />} />
                    <Route path="materials" element={<AdminMaterialLibrary />} />
                    <Route path="quality"   element={<AdminQuality />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="feature-flags" element={<AdminFeatureFlags />} />
                  </Route>
                </Route>

                {/* Manufacturer portal (auth + manufacturer user) */}
                <Route element={<RequireManufacturer />}>
                  <Route path="/manufacturer" element={<Navigate to="/manufacturer/overview" replace />} />
                  <Route element={<ManufacturerLayout />}>
                    <Route path="/manufacturer/overview"   element={<ManufacturerOverview />} />
                    <Route path="/manufacturer/products"   element={<ManufacturerProducts />} />
                    <Route path="/manufacturer/variants"   element={<ManufacturerVariants />} />
                    <Route path="/manufacturer/dealers"    element={<ManufacturerDealers />} />
                    <Route path="/manufacturer/geography"  element={<ManufacturerGeography />} />
                    <Route path="/manufacturer/reports"    element={<ManufacturerReports />} />
                    <Route path="/manufacturer/benchmarks" element={<ManufacturerBenchmarks />} />
                  </Route>
                </Route>

                {/* Demo — public, no nav/footer, standalone AR page */}
                <Route path="/demo/:productId" element={<Demo />} />

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>

            {/* Always mounted */}
            <Analytics />
            </ImpersonationProvider>
          </AppProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
